import React, { useContext, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import Swal from '../../../utils/swal';
import AppContext from "../../../../appContext";
import { useApi } from "../../../../hooks/useApi";
import CustomButton from "../../../../components/buttons/button";
import { CloudUpload, Image, X } from "react-bootstrap-icons";
import { TextField, Tooltip, InputAdornment } from '@material-ui/core';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';

const CarrosselForm = ({ image, labCnpj, onClose, onSave, onSaveComplete, getImageUrl }) => {
	const context = useContext(AppContext);
	const { post } = useApi();
	const [errors, setErrors] = useState({});
	const [loading, setLoading] = useState(false);
	const [isDragging, setIsDragging] = useState({ thumbnail: false, arquivo: false });
	const [showTooltipArquivo, setShowTooltipArquivo] = useState(false);
	const [showTooltipLink, setShowTooltipLink] = useState(false);
	const tooltipArquivoTimeout = useRef(null);
	const tooltipLinkTimeout = useRef(null);

	const [formData, setFormData] = useState({
		nome: image?.nome || '',
		link: image?.link || '',
		thumbnailFile: null,
		arquivoFile: null,
		thumbnailPreview: image?.id ? getImageUrl(image.id, 'thumbnail') : null,
		arquivoPreview: image?.id ? getImageUrl(image.id, 'arquivo') : null,
		originalFilenameThumbnail: image?.original_filename_thumbnail || null,
		originalFilenameArquivo: image?.original_filename_arquivo || null
	});

	const isValidUrl = (string) => {
		if (!string || string.trim() === '') return true;
		try {
			const url = new URL(string);
			return url.protocol === 'http:' || url.protocol === 'https:';
		} catch (_) {
			return false;
		}
	};

	const validateImageDimensions = (file) => {
		return new Promise((resolve) => {
			const img = new window.Image();
			img.onload = () => {
				URL.revokeObjectURL(img.src);
				resolve({ width: img.width, height: img.height });
			};
			img.onerror = () => {
				URL.revokeObjectURL(img.src);
				resolve(null);
			};
			img.src = URL.createObjectURL(file);
		});
	};

	const validateForm = () => {
		const newErrors = {};

		if (!formData.nome.trim()) {
			newErrors.nome = 'Nome da peça é obrigatório';
		}

		if (!image && !formData.thumbnailFile) {
			newErrors.thumbnail = 'Thumbnail é obrigatório';
		}

		if (!image && !formData.arquivoFile) {
			newErrors.arquivo = 'Arquivo da peça é obrigatório';
		}

		if (formData.arquivoFile) {
			const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
			if (!allowedTypes.includes(formData.arquivoFile.type)) {
				newErrors.arquivo = 'Formato não suportado. Use JPG, PNG ou WEBP.';
			}

			const maxSize = 500 * 1024;
			if (formData.arquivoFile.size > maxSize) {
				newErrors.arquivo = 'O arquivo excede o tamanho máximo de 500KB.';
			}
		}

		if (formData.link.trim() && !isValidUrl(formData.link)) {
			newErrors.link = 'URL inválida. Use o formato http:// ou https://';
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleInputChange = (e) => {
		const { name, value } = e.target;
		setFormData(prev => ({ ...prev, [name]: value }));

		if (errors[name]) {
			setErrors(prev => ({ ...prev, [name]: '' }));
		}
	};

	const resetFileInput = (inputId) => {
		const input = document.getElementById(inputId);
		if (input) {
			input.value = '';
		}
	};

	const handleFileChange = async (e, fileType) => {
		const file = e.target.files[0];
		if (!file) return;

		if (fileType === 'thumbnail') {
			if (!file.type.startsWith('image/')) {
				Swal.fire('Erro', 'Selecione apenas arquivos de imagem para o thumbnail.', 'error');
				resetFileInput('thumbnailFile');
				return;
			}

			const reader = new FileReader();
			reader.onloadend = () => {
				setFormData(prev => ({
					...prev,
					thumbnailFile: file,
					thumbnailPreview: reader.result
				}));
			};
			reader.readAsDataURL(file);
		} else {
			const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
			if (!allowedTypes.includes(file.type)) {
				Swal.fire('Erro', 'Formato não suportado. Use JPG, PNG ou WEBP.', 'error');
				resetFileInput('arquivoFile');
				return;
			}

			const maxSize = 500 * 1024;
			if (file.size > maxSize) {
				Swal.fire('Erro', 'O arquivo excede o tamanho máximo de 500KB.', 'error');
				resetFileInput('arquivoFile');
				return;
			}

			const reader = new FileReader();
			reader.onloadend = () => {
				setFormData(prev => ({
					...prev,
					arquivoFile: file,
					arquivoPreview: reader.result
				}));
			};
			reader.readAsDataURL(file);
		}

		if (errors[fileType]) {
			setErrors(prev => ({ ...prev, [fileType]: '' }));
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();

		if (!validateForm()) return;

		setLoading(true);
		context.showLoading(image ? 'Atualizando imagem...' : 'Salvando imagem...');

		const formDataToSend = new FormData();
		if (formData.thumbnailFile) {
			formDataToSend.append('thumbnail', formData.thumbnailFile);
		}
		if (formData.arquivoFile) {
			formDataToSend.append('arquivo', formData.arquivoFile);
		}

		try {
			const params = new URLSearchParams({
				lab_cnpj: labCnpj,
				nome: formData.nome,
				link: formData.link || ''
			});

			if (image) {
				params.append('image_id', image.id);
			}

			const endpoint = image ? 'update_carousel_image.php' : 'create_carousel_image.php';

			const json = await post(
				`${process.env.REACT_APP_LABS_API}${endpoint}?${params}`,
				formDataToSend
			);

			if (json?.status === 200) {
				await onSave();
				context.hideLoading();
				await Swal.fire('Sucesso!', json.message, 'success');
				onClose();
			} else {
				context.hideLoading();
				await Swal.fire('Erro!', json?.message || 'Erro ao salvar imagem.', 'error');
			}
		} catch (error) {
			context.hideLoading();
			await Swal.fire('Erro!', 'Erro ao salvar imagem.', 'error');
		} finally {
			setLoading(false);
		}
	};

	const triggerFileInput = (inputId) => {
		document.getElementById(inputId).click();
	};

	const handleDragOver = (e, type) => {
		e.preventDefault();
		setIsDragging(prev => ({ ...prev, [type]: true }));
	};

	const handleDragLeave = (e, type) => {
		e.preventDefault();
		setIsDragging(prev => ({ ...prev, [type]: false }));
	};

	const handleDrop = (e, type) => {
		e.preventDefault();
		setIsDragging(prev => ({ ...prev, [type]: false }));
		const file = e.dataTransfer.files[0];
		if (file) {
			handleFileChange({ target: { files: [file] } }, type);
		}
	};

	const truncateFilename = (filename, maxLength = 30) => {
		if (!filename || filename.length <= maxLength) return filename;

		const extension = filename.lastIndexOf('.') > -1
			? filename.slice(filename.lastIndexOf('.'))
			: '';

		const nameWithoutExt = filename.slice(0, filename.lastIndexOf('.'));
		const truncatedName = nameWithoutExt.slice(0, maxLength - extension.length - 3);

		return `${truncatedName}...${extension}`;
	};

	const handleMouseEnterArquivo = () => {
		tooltipArquivoTimeout.current = setTimeout(() => {
			setShowTooltipArquivo(true);
		}, 500);
	};

	const handleMouseLeaveArquivo = () => {
		clearTimeout(tooltipArquivoTimeout.current);
		setShowTooltipArquivo(false);
	};

	const handleMouseEnterLink = () => {
		tooltipLinkTimeout.current = setTimeout(() => {
			setShowTooltipLink(true);
		}, 500);
	};

	const handleMouseLeaveLink = () => {
		clearTimeout(tooltipLinkTimeout.current);
		setShowTooltipLink(false);
	};

	return (
		<Modal show={true} onHide={onClose} size="lg" centered className="carrossel-modal">
			<Modal.Header className="carrossel-form__header">
				<h2 className="carrossel-form__title">
					{image ? 'Editar imagem' : 'Nova imagem'}
				</h2>
			</Modal.Header>
			<Modal.Body className="p-0">
				<div className="carrossel-form__wrapper">
					<form onSubmit={handleSubmit} id="carrossel-form">
						<div className="carrossel-form__section">
							<h3 className="carrossel-form__section-title">Informações da peça</h3>
							<div className="row">
								<div className="col-xs-12 col-md-6">
									<div className="carrossel-form__field">
										<TextField
											id="carrossel-form-nome-input"
											name="nome"
											label="Nome da peça*"
											variant="outlined"
											className="lp-field"
											value={formData.nome}
											onChange={handleInputChange}
											error={!!errors.nome}
											helperText={errors.nome || ""}
										/>
									</div>
								</div>
								<div className="col-xs-12 col-md-6">
									<div className="carrossel-form__field">
										<TextField
											id="carrossel-form-link-input"
											name="link"
											label="Link"
											variant="outlined"
											className="lp-field"
											value={formData.link}
											onChange={handleInputChange}
											error={!!errors.link}
											helperText={errors.link || ""}
											InputProps={{
												endAdornment: (
													<InputAdornment position="end">
														<Tooltip title="Página para a qual o usuário será redirecionado. Ao deixar este campo em branco, o cliente irá abrir a mídia em uma nova aba do navegador." placement="top" classes={{ tooltip: "carrossel-tooltip" }}>
															<InfoOutlinedIcon style={{ color: "#0066B3", cursor: "pointer" }} />
														</Tooltip>
													</InputAdornment>
												),
											}}
										/>
									</div>
								</div>
							</div>
						</div>

						<div className="carrossel-form__section">
							<h3 className="carrossel-form__section-title">Arquivos da peça</h3>
							<div className="row">
								<div className="col-xs-12 col-md-6">
									<div className="carrossel-form__field">
										<div className="carrossel-form__label-with-tooltip">
										<label className="carrossel-form__label">Thumbnail da peça*:</label>
									</div>
										<div
											className={`carrossel-form__dropzone ${isDragging.thumbnail ? 'dragging' : ''}`}
											onDragOver={(e) => handleDragOver(e, 'thumbnail')}
											onDragLeave={(e) => handleDragLeave(e, 'thumbnail')}
											onDrop={(e) => handleDrop(e, 'thumbnail')}
											onClick={() => triggerFileInput('thumbnailFile')}
											id="carrossel-form-thumbnail-dropzone">
											<input
												type="file"
												id="thumbnailFile"
												className="d-none"
												accept="image/*"
												onChange={(e) => handleFileChange(e, 'thumbnail')}
											/>

											{formData.thumbnailPreview ? (
												<div className="text-center">
													<img
														src={formData.thumbnailPreview}
														alt="Preview"
														className="carrossel-form__preview-small"
													/>
													<p className="carrossel-form__filename">
														{truncateFilename(formData.thumbnailFile?.name || formData.originalFilenameThumbnail) || 'Arquivo carregado'}
													</p>
												</div>
											) : (
												<div className="text-center">
													<Image className="carrossel-form__file-icon"/>
													<p className="mb-0">Arraste ou clique para carregar</p>
												</div>
											)}
										</div>
										{errors.thumbnail && <div className="text-danger small mt-1">{errors.thumbnail}</div>}
									</div>
								</div>
								<div className="col-xs-12 col-md-6">
									<div className="carrossel-form__field">
										<div className="carrossel-form__label-with-tooltip">
											<label className="carrossel-form__label">Arquivo da peça*:</label>
											<Tooltip title="Imagem da peça que será exibida no carrossel. Ele comporta até 10 imagens. Formatos de imagem suportados: JPG, PNG, WEBP. Tamanho limite: 500kb" placement="right" classes={{ tooltip: "carrossel-tooltip" }}>
												<InfoOutlinedIcon style={{ color: "#0066B3", cursor: "pointer", fontSize: 18 }} />
											</Tooltip>
										</div>
										<div className="position-relative">
											<div
												className={`carrossel-form__dropzone ${isDragging.arquivo ? 'dragging' : ''}`}
												onDragOver={(e) => handleDragOver(e, 'arquivo')}
												onDragLeave={(e) => handleDragLeave(e, 'arquivo')}
												onDrop={(e) => handleDrop(e, 'arquivo')}
												onClick={() => triggerFileInput('arquivoFile')}
												onMouseEnter={handleMouseEnterArquivo}
												onMouseLeave={handleMouseLeaveArquivo}
												id="carrossel-form-arquivo-dropzone">
												<input
													type="file"
													id="arquivoFile"
													className="d-none"
													accept="image/jpeg,image/png,image/webp"
													onChange={(e) => handleFileChange(e, 'arquivo')}
												/>

												{formData.arquivoPreview ? (
													<div className="text-center">
														<img
															src={formData.arquivoPreview}
															alt="Preview"
															className="carrossel-form__preview-small"
														/>
														<p className="carrossel-form__filename">
															{truncateFilename(formData.arquivoFile?.name || formData.originalFilenameArquivo) || 'Arquivo carregado'}
														</p>
													</div>
												) : (
													<div className="text-center">
														<CloudUpload className="carrossel-form__file-icon"/>
														<p className="mb-0">Arraste ou clique para carregar</p>
													</div>
												)}
											</div>
										</div>
										{errors.arquivo && <div className="text-danger small mt-1">{errors.arquivo}</div>}
									</div>
								</div>
							</div>
						</div>

					</form>
				</div>
			</Modal.Body>
			<Modal.Footer className="carrossel-form__footer">
				<div className="carrossel-form__cancel-button">
					<CustomButton
						label="Cancelar"
						width="140px"
						blue_button={true}
						onClick={onClose}
						type="button"
						marginTop="0"
						id="carrossel-form-cancel-btn"
					/>
				</div>
				<div className="carrossel-form__save-button">
					<CustomButton
						onClick={handleSubmit}
						label={loading ? 'Salvando...' : 'Salvar'}
						width="140px"
						blue_button={true}
						type="submit"
						disabled={loading}
						marginTop="0"
						id="carrossel-form-save-btn"
					/>
				</div>
			</Modal.Footer>
		</Modal>
	);
};

export default CarrosselForm;